'use client';

import { useRef } from 'react';
import { Provider } from 'react-redux';

import { authReducerName } from '@/store/reducers/auth-reducer';
import { generalReducerName } from '@/store/reducers/general-reducer';
import { AppStore, makeStore } from '@/store/store';

type Props = {
  children: React.ReactNode;
  user?: CustomerPortalUser;
  subdomain?: string;
};

/**
 * Scoped Redux Provider for authenticated (main) routes.
 * Pre-seeds `auth.user` and `general.subdomain` from server-side session data
 * so that both values are available from the very first render — on the server
 * SSR pass and on the client hydration pass alike (the props are serialised
 * through the RSC payload and are therefore identical on both sides).
 *
 * This inner Provider shadows the root-level StoreProvider via React context,
 * so all components inside (main) use this pre-populated store.
 */
const AuthStoreProvider: React.FC<Props> = ({ children, user, subdomain }) => {
  const storeRef = useRef<AppStore>();

  if (!storeRef.current) {
    storeRef.current = makeStore({
      [authReducerName]: { user },
      [generalReducerName]: { subdomain: subdomain ?? '' }
    });
  }

  return <Provider store={storeRef.current}>{children}</Provider>;
};

export default AuthStoreProvider;
